import { useCallback, useEffect, useMemo, useState } from 'react'

import {
  GetServerSidePropsContext,
  InferGetServerSidePropsType,
  NextPage,
} from 'next'
import Head from 'next/head'
import Link from 'next/link'
import { useRouter } from 'next/router'

import { cx } from '@linaria/core'
import { useErrorBoundary } from 'react-error-boundary'
import InfiniteScroll from 'react-infinite-scroll-component'
import { useLocalStorage } from 'react-use'

import {
  dinnerApplyDinner,
  dinnerApproveDinnerInvitation,
  dinnerGetDinner,
  useDinnerDinners,
  useDinnerParticipation,
} from '~/api-client/food_api/dinner'
import { Dinner } from '~/api-definition/definition'
import { DinnerMemberState } from '~/api-definition/enumTypes'
import { BottoFloatActionButton } from '~/components/action/BottomFloatingActionButton'
import { Button, ButtonGroup } from '~/components/action/Button'
import { CampaignBannerLink } from '~/components/action/CampaignBannerLink'
import { Modal } from '~/components/action/Modal'
import { AppImage } from '~/components/data-display/AppImage'
import { Heading } from '~/components/data-display/Heading'
import {
  InfiniteScrollLoader,
  ListContentLoader,
} from '~/components/data-display/Loading'
import { Checkbox } from '~/components/data-input/Checkbox'
import { SelectV2 } from '~/components/data-input/SelectV2'
import { DinnerSummary } from '~/components/food_app/dinner/DinnerSummary'
import { MypageIcon } from '~/components/icons'
import { Container, ContainerInset } from '~/components/layout/Container'
import { Footer } from '~/components/layout/Footer'
import { Header } from '~/components/layout/Header'
import { formatDate, formatTime, isSameDate } from '~/libs/date'
import {
  dinnerOrganizer,
  dinnerOrganizerDisplayName,
  dinnerRestaurantName,
} from '~/libs/dinner'
import { hasMorePage, nextPage } from '~/libs/paging'
import { userProfileIconOpt } from '~/libs/user'
import { useSession } from '~/provider/SessionProvider'

type CampaignBanner = {
  startTime?: number
  endTime: number
  campaignName: string
  campaignLink: string
  imageUrl: string
  imageWidth: number
  imageHeight: number
}
const campaignBanners: CampaignBanner[] = [
  {
    endTime: new Date(2025, 2 - 1, 25, 0, 0).getTime(),
    campaignName: '202502_reserve',
    campaignLink: 'https://www.foodies-reserve.com',
    imageUrl: '/images/campaign_banners/202502_reserve.png',
    imageWidth: 1500,
    imageHeight: 400,
  },
  {
    startTime: new Date(2025, 2 - 1, 26, 0, 0).getTime(),
    endTime: new Date(2025, 3 - 1, 3, 0, 0).getTime(),
    campaignName: '202502_recipe_ru',
    campaignLink: 'https://www.recipe-ru.com/',
    imageUrl: '/images/campaign_banners/20250225_recipe_ru.png',
    imageWidth: 1500,
    imageHeight: 400,
  },
  {
    startTime: new Date(2025, 3 - 1, 5, 0, 0).getTime(),
    endTime: new Date(2025, 3 - 1, 8, 0, 0).getTime(),
    campaignName: '20250305_daylight',
    campaignLink:
      'https://www.foodies-reserve.com/restaurants/RS54ELV7QJO1Y8OK',
    imageUrl: '/images/campaign_banners/20250305_daylight.png',
    imageWidth: 1500,
    imageHeight: 400,
  },
  {
    startTime: new Date(2025, 3 - 1, 10, 0, 0).getTime(),
    endTime: new Date(2025, 3 - 1, 16, 0, 0).getTime(),
    campaignName: '202503_jirowineshop',
    campaignLink: 'https://jirowineshop.myshopify.com/collections/all',
    imageUrl: '/images/campaign_banners/202503_jirowineshop.png',
    imageWidth: 1500,
    imageHeight: 400,
  },
  {
    startTime: new Date(2025, 3 - 1, 20, 0, 0).getTime(),
    endTime: new Date(2025, 4 - 1, 1, 0, 0).getTime(),
    campaignName: '202503_reserve',
    campaignLink: 'https://www.foodies-reserve.com',
    imageUrl: '/images/campaign_banners/202503_reserve.png',
    imageWidth: 1500,
    imageHeight: 400,
  },
  {
    startTime: new Date(2025, 4 - 1, 1, 0, 0).getTime(),
    endTime: new Date(2025, 5 - 1, 1, 0, 0).getTime(),
    campaignName: '202504_reserve',
    campaignLink: 'https://www.foodies-reserve.com',
    imageUrl: '/images/campaign_banners/202504_reserve.png',
    imageWidth: 1500,
    imageHeight: 400,
  },
  {
    startTime: new Date(2025, 5 - 1, 12, 0, 0).getTime(),
    endTime: new Date(2025, 6 - 1, 4, 0, 0).getTime(),
    campaignName: '202505_reserve',
    campaignLink: 'https://www.foodies-reserve.com',
    imageUrl: '/images/campaign_banners/202505_reserve.png',
    imageWidth: 1500,
    imageHeight: 400,
  },
  {
    startTime: new Date(2025, 6 - 1, 4, 0, 0).getTime(),
    endTime: new Date(2025, 6 - 1, 10, 0, 0).getTime(),
    campaignName: '202506_akasaka_kappo_washi',
    campaignLink:
      'https://www.foodies-reserve.com/restaurants/RS5XMEVMQRXG9O6P',
    imageUrl: '/images/campaign_banners/202506_akasaka_kappo_washi.png',
    imageWidth: 1500,
    imageHeight: 400,
  },
]

// 通年 Reserve バナー
// 無効化する時は undefined にする
const fallbackCampaignBanner:
  | Omit<CampaignBanner, 'startTime' | 'endTime'>
  | undefined = {
  campaignName: 'reserve',
  campaignLink: 'https://www.foodies-reserve.com',
  imageUrl: '/images/campaign_banners/reserve.png',
  imageWidth: 1500,
  imageHeight: 400,
}

const InviteTitle = (props: { dinnerMemberState?: DinnerMemberState }) => {
  switch (props.dinnerMemberState) {
    case 'active':
    case 'applied':
      return <span>参加または応募済みの食事会です</span>
    case 'invited':
      return <span>食事会に招待されています</span>
    case 'rejected':
    case 'unapplied':
      return <span>食事会に招待されています</span>
    default:
      return <span />
  }
}

const InviteActionButtons = (props: {
  dinnerId?: string
  dinnerMemberState?: DinnerMemberState
}) => {
  const router = useRouter()
  const session = useSession({ loginRequired: true })
  const accessToken = session.response?.data.accessToken
  const { showBoundary } = useErrorBoundary()

  switch (props.dinnerMemberState) {
    case 'active':
    case 'applied':
      return (
        <div className="grid grid-cols-2 gap-x-4">
          <Link href="/dinners" className="outline-none" replace>
            <Button
              type="button"
              variant="functionWeak"
              className="w-full"
              shrink
            >
              申し込まない
            </Button>
          </Link>
          <Link
            href={`/dinners/${props.dinnerId}`}
            className="outline-none"
            replace
          >
            <Button type="button" variant="primary" className="w-full" shrink>
              申し込む
            </Button>
          </Link>
        </div>
      )
    case 'invited':
      return (
        <div className="grid grid-cols-2 gap-x-4">
          <Link href="/dinners" className="outline-none" replace>
            <Button type="button" variant="functionWeak">
              参加しない
            </Button>
          </Link>
          <Button
            type="button"
            variant="primary"
            disabled={accessToken == null || props.dinnerId == null}
            onClick={async () => {
              try {
                if (accessToken != null && props.dinnerId != null) {
                  await dinnerApproveDinnerInvitation({
                    accessToken: accessToken,
                    body: {
                      dinner_id: props.dinnerId,
                    },
                  })
                  await router.replace(`/dinners/${props.dinnerId}`)
                }
              } catch (e) {
                showBoundary(e)
              }
            }}
          >
            参加する
          </Button>
        </div>
      )
    case 'rejected':
    case 'unapplied':
      return (
        <div className="grid grid-flow-row grid-cols-2 gap-x-4">
          <Link href="/dinners" className="outline-none" replace>
            <Button type="button" variant="functionWeak">
              応募しない
            </Button>
          </Link>
          <Button
            type="button"
            variant="primary"
            disabled={accessToken == null || props.dinnerId == null}
            onClick={async () => {
              try {
                if (accessToken != null && props.dinnerId != null) {
                  await dinnerApplyDinner({
                    accessToken: accessToken,
                    body: {
                      dinner_id: props.dinnerId,
                    },
                  })
                  await router.replace(`/dinners/${props.dinnerId}`)
                }
              } catch (e) {
                showBoundary(e)
              }
            }}
          >
            応募する
          </Button>
        </div>
      )
    default:
      return <span />
  }
}

const InvitedModal = (props: { dinner: Dinner }) => {
  const router = useRouter()
  const dinnerParticipation = useDinnerParticipation(
    props.dinner
      ? {
          searchParams: {
            dinner_id: props.dinner.dinnerId,
          },
        }
      : undefined
  )

  return (
    <Modal
      // Modal デフォルトの px-8 だと iPhone SE で「詳細を確認する」に改行が入ってしまう。
      // Figma 上でも招待モーダルは 15px なので px-4 を指定する
      px="px-4"
      isOpen={dinnerParticipation.data != null}
      title={
        <InviteTitle
          dinnerMemberState={
            dinnerParticipation.data?.participation.memberState
          }
        />
      }
      setIsOpen={() => router.push('/dinners')}
      body={
        <div className={cx('flex gap-4 py-4 px-3 bg-primary-cv2 rounded')}>
          <div className="min-w-[75px]">
            <AppImage
              variant={{
                type: 'profile',
                user: dinnerOrganizer(props.dinner)?.user,
              }}
              className="rounded-full"
              rounded="full"
              width={75}
              height={75}
              alt={`${
                dinnerOrganizer(props.dinner)?.user.userName
              }さんのプロフィール画像`}
              src={userProfileIconOpt(dinnerOrganizer(props.dinner)?.user)}
            />
          </div>
          <div className="flex flex-col overflow-hidden gap-y-1">
            <div className="flex flex-grow-0 gap-x-1">
              <span className="font-bold truncate">
                {dinnerOrganizerDisplayName(props.dinner)}
              </span>
              <span className="min-w-[4em]">さんから</span>
            </div>
            <div className="font-bold">
              {formatDate(
                {
                  sec: props.dinner.scheduledAt,
                },
                {
                  sep: 'japanese',
                  showDayOfWeek: true,
                }
              )}{' '}
              {formatTime({ sec: props.dinner.scheduledAt })}
              <br />
              {dinnerRestaurantName(props.dinner)}
              <br />
              {props.dinner.dinnerName}
            </div>
            <div>の食事会に招待されています</div>
          </div>
        </div>
      }
      actionButtons={
        <>
          <p className="text-xs">・食事会に申し込むと幹事の方に通知されます</p>
          <p className="text-xs">
            ・幹事の方が参加承認をすると食事会参加が確定します
          </p>
          <p className="text-xs">・参加確定後のキャンセルは禁止しています</p>

          <div className="mt-8">
            <InviteActionButtons
              dinnerId={props.dinner.dinnerId}
              dinnerMemberState={
                dinnerParticipation.data?.participation.memberState
              }
            />
          </div>
        </>
      }
    />
  )
}

type MemberFilter = 'organized' | 'attended'
type SortBy = 'created_at_desc'

const getUrl = (args: {
  sortBy?: string
  memberFilter?: string
}): {
  pathname: string
  query: { member_filter?: MemberFilter; sort_by?: SortBy }
} => {
  const query: { member_filter?: MemberFilter; sort_by?: SortBy } = {}

  switch (args.sortBy) {
    case 'created_at_desc':
      query.sort_by = args.sortBy
      break
    case undefined:
      break
  }
  switch (args.memberFilter) {
    case 'organized':
    case 'attended':
      query.member_filter = args.memberFilter
      break
    case undefined:
      break
  }
  return {
    pathname: '/dinners',
    query: query,
  }
}

const useSortByQuery = (): SortBy | undefined => {
  const router = useRouter()
  switch (router.query.sort_by) {
    case 'created_at_desc':
      return router.query.sort_by
  }
}

const useMemberFilterQuery = (): MemberFilter | undefined => {
  const router = useRouter()
  switch (router.query.member_filter) {
    case 'organized':
    case 'attended':
      return router.query.member_filter
  }
}

const NoContent: React.FC<{
  memberFilter: MemberFilter | undefined
}> = (props) => {
  switch (props.memberFilter) {
    case 'organized':
      return (
        <>
          <p>開催予定の食事会はありません。</p>
          <p>新しく食事会を募集したい場合は、</p>
          <p>右下の＋ボタンから食事会の募集ができます。</p>
        </>
      )
    case 'attended':
      return (
        <>
          <p>参加予定の食事会はありません。</p>
        </>
      )
    default:
      return (
        <>
          <p>登録された食事会がありません。</p>
          <p>新しく食事会を募集したい場合は、</p>
          <p>右下の＋ボタンから食事会の募集ができます。</p>
        </>
      )
  }
}

const CampaignBanner = () => {
  const currentTime = Date.now()

  const activeCampaignBanner =
    campaignBanners.find(
      ({ startTime = currentTime, endTime }) =>
        currentTime >= startTime && currentTime <= endTime
    ) ?? fallbackCampaignBanner

  return (
    <>
      {activeCampaignBanner && (
        <CampaignBannerLink
          campaignName={activeCampaignBanner.campaignName}
          campaignLink={activeCampaignBanner.campaignLink}
          imageUrl={activeCampaignBanner.imageUrl}
          imageWidth={activeCampaignBanner.imageWidth}
          imageHeight={activeCampaignBanner.imageHeight}
          disableMargin
        />
      )}
    </>
  )
}

const Page: NextPage<InferGetServerSidePropsType<typeof getServerSideProps>> = (
  props
) => {
  const router = useRouter()
  const sortBy = useSortByQuery()
  const memberFilter = useMemberFilterQuery()

  const [onlySeatsLeftAll, setOnlySeatsLeftAll] = useLocalStorage(
    'dinners_onlySeatsLeft_all',
    true
  )
  const [onlySeatsLeftOrganized, setOnlySeatsLeftOrganized] = useState(false)
  const [onlySeatsLeftAttended, setOnlySeatsLeftAttended] = useState(false)

  // 幹事、参加予定タブの場合は、空席ありのチェックボックスを外す
  useEffect(() => {
    if (memberFilter === 'organized') {
      setOnlySeatsLeftOrganized(false)
    } else if (memberFilter === 'attended') {
      setOnlySeatsLeftAttended(false)
    }
  }, [memberFilter])

  const getOnlySeatsLeft = (): boolean => {
    switch (memberFilter) {
      case undefined:
        return onlySeatsLeftAll ?? true
      case 'organized':
        return onlySeatsLeftOrganized ?? false
      case 'attended':
        return onlySeatsLeftAttended ?? false
    }
  }

  const setOnlySeatsLeft = (value: boolean) => {
    switch (memberFilter) {
      case undefined:
        setOnlySeatsLeftAll(value)
        break
      case 'organized':
        setOnlySeatsLeftOrganized(value)
        break
      case 'attended':
        setOnlySeatsLeftAttended(value)
        break
    }
  }

  const onlySeatsLeft = getOnlySeatsLeft()

  const dinners = useDinnerDinners({
    searchParams: {
      member_filter: memberFilter,
      sort_by: sortBy,
      only_seats_left: onlySeatsLeft,
    },
  })

  const flatDinners = useMemo(
    () => dinners.data?.flatMap((dinners) => dinners.dinners),
    [dinners.data]
  )

  const resetDinners = useCallback(() => {
    dinners.setSize(1)
    dinners.mutate()
  }, [dinners])

  return (
    <div className="min-h-full">
      <Head>
        <title>募集中の食事会</title>
      </Head>

      <Header>
        <div />
        募集中の食事会
        <div />
      </Header>

      {props.invitedDinnerResponse && (
        <InvitedModal dinner={props.invitedDinnerResponse.dinner} />
      )}

      <CampaignBanner />

      <InfiniteScroll
        dataLength={flatDinners?.length ?? 0}
        next={() => nextPage(dinners)}
        hasMore={hasMorePage(dinners)}
        loader={<InfiniteScrollLoader />}
      >
        <Container>
          <ContainerInset>
            <div className="flex items-center justify-end mb-4">
              <MypageIcon className="mr-1 fill-black" />
              <p className="text-sm text-right text-black">
                <Link className="underline" href="/account/dinners">
                  自分に関連する食事会一覧
                </Link>
              </p>
            </div>

            <div className="flex">
              <div className="flex-auto mb-2">
                <ButtonGroup>
                  <Link
                    href={getUrl({ sortBy })}
                    className="flex justify-center"
                  >
                    <Button
                      variant="functionWeak"
                      shrink={true}
                      fontSize="sm"
                      data-current={memberFilter == null}
                      disabled={dinners.isLoading}
                      onClick={resetDinners}
                    >
                      すべて
                    </Button>
                  </Link>
                  <Link
                    href={getUrl({ sortBy, memberFilter: 'organized' })}
                    className="flex justify-center"
                  >
                    <Button
                      variant="functionWeak"
                      shrink={true}
                      fontSize="sm"
                      data-current={memberFilter === 'organized'}
                      disabled={dinners.isLoading}
                      onClick={resetDinners}
                    >
                      幹事
                    </Button>
                  </Link>
                  <Link
                    href={getUrl({ sortBy, memberFilter: 'attended' })}
                    className="flex justify-center"
                  >
                    <Button
                      variant="functionWeak"
                      shrink={true}
                      fontSize="sm"
                      data-current={memberFilter === 'attended'}
                      disabled={dinners.isLoading}
                      onClick={resetDinners}
                    >
                      参加予定
                    </Button>
                  </Link>
                </ButtonGroup>
              </div>
              <div className="flex-none ml-2 text-right">
                <SelectV2
                  name="sortBy"
                  bgFunctionCV2
                  disabled={dinners.isLoading}
                  onChange={async (v) => {
                    await router.push(getUrl({ sortBy: v, memberFilter }))
                  }}
                  defaultValue=""
                  options={[
                    { value: '', label: '日付順' },
                    { value: 'created_at_desc', label: '新着順' },
                  ]}
                />
              </div>
            </div>

            {dinners.data?.[0]?.paging !== undefined &&
              dinners.data[0].paging.all > 0 && (
                <>
                  <div className="mb-4">
                    <Checkbox
                      id="is_available_seat"
                      label="空席あり"
                      checked={onlySeatsLeft}
                      onCheckedChange={(v) => {
                        v !== 'indeterminate' && setOnlySeatsLeft(v)
                        resetDinners()
                      }}
                      disabled={dinners.isLoading}
                    />
                  </div>
                  <div className="flex justify-between mb-2 text-sm">
                    <div className="flex">
                      <div className="mr-2">表示内容:</div>
                      <div className="flex items-center font-bold gap-x-1">
                        <div>
                          {memberFilter == null ? 'すべての食事会' : ''}
                          {memberFilter === 'organized' ? '幹事の食事会' : ''}
                          {memberFilter === 'attended'
                            ? '参加予定の食事会'
                            : ''}
                        </div>
                        <div>
                          {sortBy == null ? '日付順' : ''}
                          {sortBy === 'created_at_desc' ? '新着順' : ''}
                        </div>
                        <div>{onlySeatsLeft ? '空席あり' : ''}</div>
                      </div>
                    </div>
                    <div className="font-bold">
                      全{dinners.data?.[0]?.paging.all}件
                    </div>
                  </div>
                </>
              )}

            {dinners.data?.[0]?.paging.all === 0 && (
              <div className="mt-8 text-sm font-bold leading-5 text-center">
                <NoContent memberFilter={memberFilter} />
              </div>
            )}
            {dinners.isLoading && <ListContentLoader />}
            <div className="flex flex-col gap-y-8">
              {flatDinners?.map((dinner, i, dinners) => {
                const prevDinner = i === 0 ? null : dinners[i - 1]
                const nextDinner =
                  i === dinners.length - 1 ? null : dinners[i + 1]

                return (
                  <div key={dinner.dinnerId}>
                    {!(
                      prevDinner &&
                      isSameDate(
                        { sec: dinner.scheduledAt },
                        { sec: prevDinner.scheduledAt }
                      )
                    ) && (
                      <div className="mb-5">
                        <Heading variant="h2" component="h2">
                          {formatDate(
                            { sec: dinner.scheduledAt },
                            { omitCurrentYear: true, showDayOfWeek: true }
                          )}
                        </Heading>
                      </div>
                    )}

                    <Link href={`/dinners/${dinner.dinnerId}`}>
                      <DinnerSummary
                        showParticipationStatus
                        dinner={dinner}
                        showDate={true}
                      />

                      {nextDinner &&
                        isSameDate(
                          { sec: dinner.scheduledAt },
                          { sec: nextDinner.scheduledAt }
                        ) && (
                          <div
                            className={cx(
                              'w-full mt-5',
                              dinners.length - 1 !== i && 'border-b border-gray'
                            )}
                          />
                        )}
                    </Link>
                  </div>
                )
              })}
            </div>
          </ContainerInset>
        </Container>
      </InfiniteScroll>

      <BottoFloatActionButton href="/dinners/new" />

      <Footer />
    </div>
  )
}

export const getServerSideProps = async (
  context: GetServerSidePropsContext
) => {
  const props = {
    invitedDinnerResponse:
      typeof context.query.dinner_invitation_code === 'string'
        ? await dinnerGetDinner({
            searchParams: {
              dinner_invitation_code: context.query.dinner_invitation_code,
            },
          })
        : null,
  }
  return { props }
}

export default Page
